import { useCallback, useMemo, useState } from "react";
import { SvgIcon } from "@/components";
import { getIconByAsset, getUsdAmount } from "@/helpers";

import styles from "./AmlCheckAssets.module.sass";

type AssetsItemType = {
  balance_approximate: number | null;
  balance_usd: number | null;
  token_name: string;
  token_symbol: string;
  token_address: string;
};

interface AmlCheckAssetsProps {
  protocol: string;
  assets: AssetsItemType[];
}

const Item = ({
  balance_approximate,
  balance_usd,
  token_name,
  token_symbol,
}: AssetsItemType) => {
  const iconUrl = getIconByAsset(token_symbol);

  return (
    <div className={styles.item}>
      <div className={styles.item__icon}>
        <img src={iconUrl} alt="" />
      </div>
      <div className={styles.item__body}>
        <div className={styles.item__abbr}>{token_name}</div>
        <div className={styles.item__name}>{token_symbol}</div>
      </div>
      <div className={styles.item__info}>
        <div className={styles.item__value}>
          {balance_approximate?.toFixed(2)}
        </div>
        <div className={styles.item__fiat}>
          {getUsdAmount(balance_usd || 0)}
        </div>
      </div>
    </div>
  );
};

const AmlCheckAssets = ({ protocol, assets }: AmlCheckAssetsProps) => {
  const amount = assets.reduce((total, asset) => {
    return total + (asset.balance_usd ? Number(asset.balance_usd) : 0);
  }, 0);
  const [showMore, setShowMore] = useState(false);

  const handleShowMore = useCallback(() => {
    setShowMore(true);
  }, []);

  const dataAssets = useMemo(() => {
    if (!showMore) {
      return assets.slice(0, 4);
    }

    return assets;
  }, [assets, showMore]);

  return (
    <div className={styles.assets}>
      <div className={styles.assets__head}>
        <div className={styles.assets__info}>
          <div className={styles.assets__title}>{protocol} balance</div>
          <div className={styles.assets__value}>{getUsdAmount(amount)}</div>
        </div>
        {assets.length > 0 && (
          <div className={styles.assets__badge}>{assets.length} assets</div>
        )}
      </div>

      <div className={styles.assets__scroll}>
        <div className={styles.assets__list}>
          {dataAssets.length &&
            dataAssets.map((asset) => (
              <Item
                key={asset.token_address}
                token_address={asset.token_address}
                balance_approximate={asset.balance_approximate}
                balance_usd={asset.balance_usd}
                token_name={asset.token_name}
                token_symbol={asset.token_symbol}
              />
            ))}
        </div>
      </div>

      {assets.length > 4 && !showMore && (
        <div className={styles.assets__actions}>
          <button
            type="button"
            className={styles.assets__more}
            onClick={handleShowMore}
          >
            <span>Show more</span>
            <SvgIcon name="arrow-down-short" />
          </button>
        </div>
      )}
    </div>
  );
};

export default AmlCheckAssets;

export type { AssetsItemType };
